import { useState } from 'react';
import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import toast from 'react-hot-toast';
import useToggle from '../../hooks/use-toggle';

const useDutiesWeeksService = () => {
  const [dutiesWeeks, setDutiesWeeks] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, toggleLoading] = useToggle();

  const fetchDutiesWeeks = async (_page, _size, status = null) => {
    const page = _page ?? 0;
    const size = _size ?? 100;

    toggleLoading();
    try {
      const url = status
        ? `${endpoints.dutiesWeeks.getDutiesWeeks}?page=${page}&size=${size}&status=${status}`
        : `${endpoints.dutiesWeeks.getDutiesWeeks}?page=${page}&size=${size}`;

      const response = await axiosInstance.get(url);
      const { data } = response.data;

      setTotalRecords(data.records || 0);

      setDutiesWeeks(
        Array.isArray(data.data)
          ? data.data.map((week) => ({
              id: week.id,
              weekName: week.weekName,
              weekStartDate: week.weekStartDate,
              weekEndDate: week.weekEndDate,
              termId: week.termId,
              termName: week.termName,
              status: week.status,
              createdAt: week.createdAt,
            }))
          : []
      );
    } catch (error) {
      console.error('Fetch DutiesWeeks Error:', error);
      toast.error(error.response?.data?.message || 'Error fetching duties weeks');
    } finally {
      toggleLoading();
    }
  };

  const fetchDutiesWeeksByTerm = async (termId) => {
    if (!termId) {
      setDutiesWeeks([]);
      setTotalRecords(0);
      return;
    }
    
    toggleLoading();
    try {
      const response = await axiosInstance.get(`${endpoints.dutiesWeeks.getByTerm}${termId}`);
      const weeks = response.data.data || [];
      
      setDutiesWeeks(
        Array.isArray(weeks)
          ? weeks.map((week) => ({
              id: week.id,
              weekName: week.weekName,
              weekStartDate: week.weekStartDate,
              weekEndDate: week.weekEndDate,
              termId: week.termId,
              termName: week.termName,
              status: week.status,
              createdAt: week.createdAt,
            }))
          : []
      );
      setTotalRecords(weeks.length);
    } catch (error) {
      console.error('Fetch DutiesWeeks By Term Error:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. You do not have permission to view duties weeks.');
      } else {
        toast.error(error.response?.data?.message || 'Error fetching duties weeks for term');
      }
      setDutiesWeeks([]);
      setTotalRecords(0);
    } finally {
      toggleLoading();
    }
  };

  const createDutiesWeek = async (dutiesWeekDto, onSuccess) => {
    try {
      // Validate required fields before sending
      if (!dutiesWeekDto.weekName || !dutiesWeekDto.termId) {
        toast.error('Week name and term are required');
        return;
      }

      console.log('Creating duties week with data:', dutiesWeekDto);
      const response = await axiosInstance.post(endpoints.dutiesWeeks.create, dutiesWeekDto);
      toast.success(response.data.message || 'Duties week created successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error creating duties week:', error);
      console.error('Error response:', error.response);
      
      if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions or contact your administrator.');
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Error creating duties week. Please try again.');
      }
    }
  };

  const loadDutiesWeekDetails = async (dutiesWeekId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(`${endpoints.dutiesWeeks.getDutiesWeek}${dutiesWeekId}`);
      return response.data.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching duties week details');
      console.error('Error fetching duties week details:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  const updateDutiesWeekDetails = async (payload) => {
    try {
      const response = await axiosInstance.put(endpoints.dutiesWeeks.update, payload);
      toast.success(response.data.message || 'Duties week updated successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating duties week');
      console.error('Error updating duties week:', error);
      throw error;
    }
  };

  const updateDutiesWeekStatus = async (dutiesWeekId, currentStatus, onSuccess) => {
    const newStatus = currentStatus === 'DISABLED' ? 'ACTIVE' : 'DISABLED';
    const apiUrl = `${endpoints.dutiesWeeks.status}?id=${dutiesWeekId}&status=${newStatus}`;

    try {
      const response = await axiosInstance.post(apiUrl);
      toast.success(response.data.message || 'Duties week status updated successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating duties week status');
      console.error('Error updating duties week status:', error);
    }
  };

  const deleteDutiesWeek = async (dutiesWeekId, onSuccess) => {
    try {
      const response = await axiosInstance.post(
        `${endpoints.dutiesWeeks.status}?id=${dutiesWeekId}&status=DELETED`
      );
      toast.success(response.data.message || 'Duties week deleted successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting duties week');
      console.error('Error deleting duties week:', error);
    }
  };

  const assignTeacherToDutiesWeek = async (dutiesWeekId, teacherId, onSuccess) => {
    try {
      const response = await axiosInstance.post(
        `${endpoints.dutiesWeeks.assignTeacher}?dutiesWeekId=${dutiesWeekId}&teacherId=${teacherId}`
      );
      toast.success(response.data.message || 'Teacher assigned successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error assigning teacher:', error);
      toast.error(error.response?.data?.message || 'Error assigning teacher');
    }
  };

  const unassignTeacherFromDutiesWeek = async (dutiesWeekId, teacherId, onSuccess) => {
    try {
      const response = await axiosInstance.post(
        `${endpoints.dutiesWeeks.unassignTeacher}?dutiesWeekId=${dutiesWeekId}&teacherId=${teacherId}`
      );
      toast.success(response.data.message || 'Teacher unassigned successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error unassigning teacher:', error);
      toast.error(error.response?.data?.message || 'Error unassigning teacher');
    }
  };

  const fetchTeachersByDutiesWeek = async (dutiesWeekId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(`${endpoints.dutiesWeeks.getTeachers}${dutiesWeekId}/teachers`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching teachers for duties week:', error);
      toast.error(error.response?.data?.message || 'Error fetching teachers');
      return [];
    } finally {
      toggleLoading();
    }
  };

  const fetchDutiesWeeksByTermWithTeachers = async (termId) => {
    toggleLoading();
    try {
      const response = await axiosInstance.get(`${endpoints.dutiesWeeks.getByTermWithTeachers}${termId}/with-teachers`);
      const weeks = response.data.data || [];
      
      setDutiesWeeks(
        Array.isArray(weeks)
          ? weeks.map((week) => ({
              id: week.id,
              weekName: week.weekName,
              weekStartDate: week.weekStartDate,
              weekEndDate: week.weekEndDate,
              termId: week.termId,
              termName: week.termName,
              status: week.status,
              createdAt: week.createdAt,
            }))
          : []
      );
      setTotalRecords(weeks.length);
    } catch (error) {
      console.error('Fetch DutiesWeeks With Teachers Error:', error);
      toast.error(error.response?.data?.message || 'Error fetching duties weeks with teachers');
      setDutiesWeeks([]);
      setTotalRecords(0);
    } finally {
      toggleLoading();
    }
  };

  return {
    dutiesWeeks,
    totalRecords,
    fetchDutiesWeeks,
    fetchDutiesWeeksByTerm,
    fetchDutiesWeeksByTermWithTeachers,
    fetchTeachersByDutiesWeek,
    createDutiesWeek,
    loadDutiesWeekDetails,
    updateDutiesWeekDetails,
    updateDutiesWeekStatus,
    deleteDutiesWeek,
    assignTeacherToDutiesWeek,
    unassignTeacherFromDutiesWeek,
    isLoading,
  };
};

export default useDutiesWeeksService;

